import React, { useState } from "react";

import { AlertTriangle, CheckCircle, XCircle } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { Client } from "~/types/client";

interface ClientActivationDialogProps {
  client: Client | null;
  isOpen: boolean;
  onClose: () => void;
  onClientUpdated: () => void;
}

export const ClientActivationDialog: React.FC<ClientActivationDialogProps> = ({
  client,
  isOpen,
  onClose,
  onClientUpdated,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deactivationReason, setDeactivationReason] = useState("");

  const isActive = client?.is_active ?? client?.isActive ?? true;
  const isDeactivating = isActive; // If client is currently active, we're deactivating
  const isActivating = !isActive; // If client is currently inactive, we're activating

  const handleSubmit = async () => {
    if (!client) return;

    // Validate deactivation reason is provided when deactivating
    if (isDeactivating && !deactivationReason.trim()) {
      toast({
        title: "Error",
        description: "Debe proporcionar una razón para desactivar el cliente.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const updateData = {
        is_active: !isActive,
        deactivation_reason: isDeactivating ? deactivationReason.trim() : null,
      };

      const { error } = await supabase
        .from("clients")
        .update(updateData)
        .eq("id", client.id);

      if (error) {
        throw error;
      }

      toast({
        title: "Cliente actualizado",
        description: `El cliente ha sido ${isDeactivating ? "desactivado" : "activado"} exitosamente.`,
      });

      onClientUpdated();
      handleClose();
    } catch (error) {
      console.error("Error updating client:", error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el estado del cliente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setDeactivationReason("");
    onClose();
  };

  if (!client) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isDeactivating ? (
              <>
                <XCircle className="h-5 w-5 text-red-500" />
                Desactivar Cliente
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5 text-green-500" />
                Activar Cliente
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isDeactivating ? (
              <>
                ¿Está seguro que desea desactivar el cliente{" "}
                <strong>{client.name}</strong>?
                <br />
                <br />
                <div className="flex items-start gap-2 rounded-md bg-yellow-50 p-3 text-sm text-yellow-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>
                    Un cliente desactivado no podrá crear nuevas notas de venta
                    hasta que sea reactivado.
                  </span>
                </div>
              </>
            ) : (
              <>
                ¿Está seguro que desea activar el cliente{" "}
                <strong>{client.name}</strong>?
                <br />
                <br />
                El cliente podrá crear nuevas notas de venta una vez activado.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {isDeactivating && (
          <div className="space-y-2">
            <Label htmlFor="deactivation-reason">
              Razón de desactivación <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="deactivation-reason"
              placeholder="Ingrese la razón por la cual está desactivando este cliente..."
              value={deactivationReason}
              onChange={(e) => setDeactivationReason(e.target.value)}
              className="min-h-[100px]"
              disabled={isSubmitting}
            />
            <p className="text-sm text-gray-500">
              Esta razón será visible en el perfil del cliente.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            variant={isDeactivating ? "destructive" : "default"}
          >
            {isSubmitting
              ? "Procesando..."
              : isDeactivating
                ? "Desactivar"
                : "Activar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
